import React from 'react';
import {
  Box,
  Typography,
  Button,
  Avatar,
  Chip,
  TextField,
  Card,
  CardContent,
  alpha,
} from '@mui/material';
import {
  Add,
  ChatBubble,
  Sort,
  SortOutlined,
} from '@mui/icons-material';
import PostCard from './PostCard';
import { useAuth } from '../../context/AuthContext';
import { Community, Post } from '../../types/community';
import { PostListSkeleton } from '../skeletons';

interface PostsTabProps {
  posts: Post[];
  postsLoading: boolean;
  community: Community;
  sortBy: string;
  setSortBy: (value: string) => void;
  postSortBy: 'desc' | 'asc';
  setPostSortBy: (value: 'desc' | 'asc') => void;
  isDark: boolean;
  goldColor: string;
  deepGreen: string;
  glassEffect: any;
  gradientButtonStyle: any;
  handleCreatePost: () => void;
  handleVote: (postId: string, vote: 'up' | 'down' | 'remove') => void;
  handlePostMenuOpen: (event: React.MouseEvent<HTMLElement>, postId: string) => void;
  navigate: any;
  toggleVideoMute: (postId: string) => void;
  handleVideoRef: (postId: string, el: HTMLVideoElement | null) => void;
  videoMuted: {[key: string]: boolean};
  isMobile: boolean;
}

const PostsTab: React.FC<PostsTabProps> = ({
  posts,
  postsLoading,
  community,
  sortBy,
  setSortBy,
  postSortBy,
  setPostSortBy,
  isDark,
  goldColor,
  deepGreen,
  glassEffect,
  gradientButtonStyle,
  handleCreatePost,
  handleVote,
  handlePostMenuOpen,
  navigate,
  toggleVideoMute,
  handleVideoRef,
  videoMuted,
  isMobile,
}) => {
  const { user } = useAuth();
  const sortOptions = [
    { id: 'new', label: 'New' },
    { id: 'hot', label: 'Hot' },
    { id: 'top', label: 'Top' }
  ];

  return (
    <Box>
      {/* Create Post & Filters Card */}
      <Card sx={{ 
        mb: { xs: 2, md: 3 },
        borderRadius: '20px',
        ...glassEffect,
        border: `1px solid ${alpha(goldColor, 0.2)}`,
        minHeight: { xs: 'auto', md: '140px' }
      }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar
              src={user?.profilePicture}
              sx={{ 
                width: { xs: 36, md: 40 }, 
                height: { xs: 36, md: 40 },
                border: `2px solid ${alpha(goldColor, 0.3)}`
              }}
            >
              {user?.username?.charAt(0).toUpperCase()}
            </Avatar>
            <TextField
              fullWidth
              placeholder="Create a post in this community..."
              onClick={handleCreatePost}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '20px',
                  bgcolor: isDark ? alpha('#fff', 0.05) : alpha(deepGreen, 0.03),
                  cursor: 'pointer',
                  '& fieldset': {
                    borderColor: isDark ? alpha(goldColor, 0.3) : alpha(deepGreen, 0.2)
                  },
                  '&:hover fieldset': {
                    borderColor: goldColor,
                  }
                },
                '& .MuiInputBase-input': { 
                  color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
                  cursor: 'pointer',
                  fontSize: { xs: '0.9rem', md: '1rem' }
                }
              }}
              InputProps={{
                readOnly: true
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {sortOptions.map((option) => (
                <Chip
                  key={option.id}
                  label={option.label}
                  size="small"
                  onClick={() => setSortBy(option.id)}
                  sx={{
                    height: { xs: 30, md: 36 },
                    borderRadius: '20px',
                    bgcolor: sortBy === option.id 
                      ? goldColor 
                      : isDark ? alpha('#fff', 0.05) : alpha(deepGreen, 0.05),
                    color: sortBy === option.id ? deepGreen : 'inherit',
                    border: `1px solid ${alpha(goldColor, 0.3)}`,
                    fontWeight: 600,
                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                    '&:hover': {
                      bgcolor: sortBy === option.id 
                        ? alpha(goldColor, 0.9) 
                        : isDark ? alpha('#fff', 0.1) : alpha(deepGreen, 0.1),
                      borderColor: goldColor,
                    }
                  }}
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                icon={postSortBy === 'desc' ? <Sort /> : <SortOutlined />}
                label={postSortBy === 'desc' ? 'Newest' : 'Oldest'}
                onClick={() => setPostSortBy(postSortBy === 'desc' ? 'asc' : 'desc')}
                sx={{
                  height: { xs: 30, md: 36 },
                  borderRadius: '20px',
                  bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(5,24,20,0.05)',
                  color: isDark ? '#fff' : deepGreen,
                  border: `1px solid ${alpha(goldColor, 0.3)}`,
                  fontWeight: 600,
                  fontSize: { xs: '0.7rem', md: '0.8rem' },
                  '&:hover': { 
                    bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
                    borderColor: goldColor,
                  },
                  '& .MuiChip-icon': {
                    color: goldColor,
                    marginLeft: { xs: '4px', md: '8px' },
                    fontSize: { xs: '0.9rem', md: '1rem' }
                  }
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Posts List */}
      {postsLoading ? (
        <PostListSkeleton />
      ) : posts.length === 0 ? (
        <Card sx={{ 
          p: { xs: 4, md: 8 }, 
          textAlign: 'center', 
          borderRadius: '20px', 
          ...glassEffect,
          border: `1px solid ${alpha(goldColor, 0.2)}`,
          minHeight: '300px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <ChatBubble sx={{ fontSize: { xs: 60, md: 80 }, color: goldColor, mb: 3, opacity: 0.5 }} />
          <Typography variant="h5" sx={{ 
            mb: 2, 
            color: isDark ? '#fff' : deepGreen, 
            fontWeight: 800,
            fontSize: { xs: '1.3rem', md: '1.5rem' }
          }}>
            No posts yet
          </Typography>
          <Typography sx={{ 
            color: isDark ? alpha('#fff', 0.6) : alpha(deepGreen, 0.6), 
            mb: 4, 
            maxWidth: 400,
            fontSize: { xs: '0.9rem', md: '1rem' }
          }}>
            Be the first to share something in this community!
          </Typography>
          <Button 
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreatePost}
            sx={{
              ...gradientButtonStyle,
              px: { xs: 3, md: 5 },
              py: { xs: 1, md: 1.5 },
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}
          >
            Create First Post
          </Button>
        </Card>
      ) : (
        <Box sx={{ 
          ...glassEffect,
          borderRadius: '20px',
          overflow: 'hidden'
        }}>
          {posts.map((post, index) => (
            <PostCard
              key={post.id}
              post={post}
              index={index}
              posts={posts}
              community={community}
              isDark={isDark}
              goldColor={goldColor}
              deepGreen={deepGreen}
              glassEffect={glassEffect}
              handleVote={handleVote}
              handlePostMenuOpen={handlePostMenuOpen}
              navigate={navigate}
              toggleVideoMute={toggleVideoMute}
              handleVideoRef={handleVideoRef}
              videoMuted={videoMuted}
              isMobile={isMobile}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PostsTab;